import { Linkified } from "../../components/Linkified";
import { Check, Clock4, Hand, Plus } from "lucide-react";
import { AccountProp } from "../../components/AccountProp";
import { useEffect, useState } from "react";
import { FactCard, formatsOf, ModalSection, ModalTitle, PersonStatus, Prop, TaskItems } from "../../components/ModalParts";
import { Chat } from "../../components/Chat";
import { Button } from "../../components/ui/button";
import { Modal } from "../../components/ui/modal";
import { ME_EDITOR, PEOPLE, taskModerated } from "../../data/people";
import { dayTaskCount, lastVersion, liveItems, PUB_FORMATS, tasksLabel } from "../../lib/domain";
import { agoLong, dayName, dayOf, dayOn, timeOf, whenLabel } from "../../lib/time";
import { useNow } from "../../lib/useNow";
import { cn } from "../../lib/utils";
import { useStore, useTask } from "../../store";
import type { Task } from "../../types";
import { ItemDraftCard, ProposeDay, PublishAfterModeration, useEditorActions, WithdrawSubmission } from "./EditorShared";
import { VideoRemove } from "../../components/VideoRemove";
import { EditTopic } from "../../components/EditTopic";
import { EARLY, EarlyPost, VideoLayout } from "../client/TaskModal";
import { DeleteTopic } from "../../components/DeleteTopic";
import { DownloadOriginal } from "../../components/DownloadOriginal";

/* ───────────────────────── Вариант A · модалка ───────────────────────── */

export function EditorTaskModal() {
  const id = useStore((s) => s.openTaskId);
  const role = useStore((s) => s.role);
  const intent = useStore((s) => s.intent);
  const close = useStore((s) => s.closeTask);
  const task = useTask(id);
  const [mode, setMode] = useState<"view" | "propose">("view");

  useEffect(() => {
    setMode(intent && intent.id === id && intent.mode === "propose" ? "propose" : "view");
  }, [id, intent]);

  const open = role === "editor" && !!task;
  // Сдал и сдавать нечего — то же окно с плеером, что у контент-мейкера
  // (08.10.2026). Новый ролик или правки — форма сдачи, как раньше.
  const live = task ? liveItems(task) : [];
  const toSubmit = !!task?.acceptedAt && live.some((i) => i.status === "assigned" || i.status === "rework");
  const review = !!task && live.some((i) => i.versions.length > 0) && !toSubmit && task.status !== "wait";
  return (
    <Modal open={open} onClose={close} width={review ? 1080 : 880} className={review ? "h-[700px]" : undefined}>
      {task && (review
        ? <VideoLayout key={`${task.id}-${task.status}`} task={task} />
        : <ModalBody key={`${task.id}-${task.status}`} task={task} mode={mode} setMode={setMode} onClose={close} />)}
    </Modal>
  );
}

function ModalBody({ task, mode, setMode, onClose }: { task: Task; mode: "view" | "propose"; setMode: (m: "view" | "propose") => void; onClose: () => void }) {
  const now = useNow();
  const actions = useEditorActions();
  const take = useStore((s) => s.take);
  const tasks = useStore((s) => s.tasks);
  const busy = useStore((s) => s.busy);
  const proposing = task.status === "wait" && mode === "propose";
  const [editing, setEditing] = useState(false);
  const canEdit = (task.editor === ME_EDITOR || task.creator === ME_EDITOR) && task.status !== "done";
  const live = liveItems(task);
  const dayCount = dayTaskCount(ME_EDITOR, task.day, tasks, busy, task.id) + live.length;
  // Что сдавать прямо сейчас: невыданное и вернувшееся. Взятый день открывает
  // их все сразу — монтажёр идёт по списку сверху вниз.
  const todo = task.acceptedAt ? live.filter((i) => i.status === "assigned" || i.status === "rework") : [];
  const submitted = live.filter((i) => i.versions.length > 0);

  const status =
    task.status === "open" ? (
      <span className="font-medium text-muted-foreground">Свободная — можно взять себе</span>
    ) : task.status === "wait" && task.proposal ? (
      <>
        <span className="block font-medium text-review-fg">
          Вы предложили {dayOn(task.proposal.day, now)}, до {timeOf(task.proposal.due)}
        </span>
        <span className="mt-1 block text-muted-foreground">Ждём ответа</span>
      </>
    ) : task.status === "wait" ? (
      <>
        <span className="font-medium text-wait-fg">Новая задача</span>
        <span className="text-muted-foreground"> · {agoLong(task.createdAt, now)}</span>
      </>
    ) : task.status === "work" ? (
      <span className="font-medium text-work-fg">В работе · сдано {submitted.length} из {live.length}</span>
    ) : task.status === "rework" ? (
      <span className="font-medium text-rework-fg">Правки · {todo.filter((i) => i.status === "rework").length}</span>
    ) : task.status === "review" ? (
      <span className="font-medium text-review-fg">На проверке · {live.filter((i) => i.status === "uploaded" || i.status === "moderated").length}</span>
    ) : task.status === "publish" ? (
      <span className="font-medium text-publish-fg">Принято, ждёт публикации</span>
    ) : (
      <span className="font-medium text-done-fg">День закрыт {task.approvedAt ? whenLabel(task.approvedAt, now) : ""}</span>
    );

  return (
    <>
      <div className="scroll-thin flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 pb-7 pt-5 md:px-7 md:pt-6">
        <ModalTitle title={task.title} onClose={onClose} onEdit={canEdit && !editing ? () => setEditing(true) : undefined} />
        {editing && <EditTopic task={task} onDone={() => setEditing(false)} />}
        {proposing ? (
          <ProposeDay task={task} onDone={onClose} onCancel={() => setMode("view")} />
        ) : (
          <div className="grid min-w-0 grid-cols-1 items-start gap-5 md:grid-cols-[minmax(0,1fr)_272px] md:gap-7">
            <div className="flex min-w-0 flex-col gap-6">
              {task.description && (
                <ModalSection title="Что снять">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed [overflow-wrap:anywhere]"><Linkified text={task.description} /></p>
                </ModalSection>
              )}
              {todo.length > 0 ? (
                <ModalSection title="Сдать" count={todo.length}>
                  <div className="flex flex-col gap-3">
                    {todo.map((it) => (
                      <ItemDraftCard key={it.id} task={task} item={it} />
                    ))}
                  </div>
                </ModalSection>
              ) : (
                live.length > 0 && (
                  <TaskItems
                    task={task}
                    briefTitle="Что сделать"
                    download
                    actions={task.editor === ME_EDITOR ? (it) => (it.versions.length === 0 || ["uploaded", "moderated", "rework"].includes(it.status) ? <VideoRemove task={task} item={it} /> : null) : undefined}
                  />
                )
              )}
              {/* Публикация — один ролик (08.10.2026): добавить второй можно только в старую тему без роликов. */}
              {task.editor === ME_EDITOR && task.acceptedAt && live.length === 0 && <AddVideoForm task={task} />}
              {submitted.length > 0 && <SubmittedList task={task} now={now} />}
              {/* Выложил раньше, чем сдал, — ролик закрывается ссылкой (25.10.2026). */}
              {task.editor === ME_EDITOR && live.filter((i) => EARLY.includes(i.status)).map((it) => <EarlyPost key={it.id} task={task} item={it} />)}
              <ModalSection title="Вопросы контент-мейкеру" count={task.chat.length}>
                <Chat task={task} me={ME_EDITOR} bare />
              </ModalSection>
              <DeleteTopic task={task} className="self-start" />
            </div>
            <FactCard>
              <PersonStatus who={task.creator} role={task.creator === ME_EDITOR ? "Своя публикация" : "Поставил"}>
                {status}
              </PersonStatus>
              <Prop label={task.period === "week" ? "Неделя" : "Дедлайн"}>
                {task.period === "week"
                  ? `${dayName(task.day, now)} — ${dayName(dayOf(task.due), now).toLowerCase()}, до ${timeOf(task.due)}`
                  : `${dayName(task.day, now)}, ${timeOf(task.due)}`}
              </Prop>
              <Prop label="Видео">{live.length ? tasksLabel(live.length) : "пока нет"}</Prop>
              <Prop label="Формат">{formatsOf(task)}</Prop>
              <AccountProp task={task} />
              {task.status === "wait" && <Prop label="Загрузка">{tasksLabel(dayCount)} в этот день</Prop>}
            </FactCard>
          </div>
        )}
      </div>
      {task.status === "open" && (
        <footer className="flex shrink-0 items-center justify-end gap-2.5 border-t px-7 py-4">
          <Button
            onClick={async () => {
              // Окно остаётся (02.11.2026): взял — сразу видно, что сдавать.
              await take(task.id);
            }}
          >
            <Hand />
            Взять себе
          </Button>
        </footer>
      )}
      {!proposing && task.status === "wait" && (
        <footer className="flex shrink-0 items-center justify-end gap-2.5 border-t px-7 py-4">
          {!task.proposal && (
            <Button variant="outline" onClick={() => setMode("propose")}>
              <Clock4 />
              Не успеваю
            </Button>
          )}
          <Button
            onClick={() => {
              // Окно остаётся и показывает «Сдать»: взял в работу — сразу грузи.
              actions.accept(task);
            }}
          >
            <Check />
            {task.proposal ? "Взять в исходный день" : "Взять в работу"}
          </Button>
        </footer>
      )}
    </>
  );
}

/** Что уже сдано и где оно сейчас: монтажёру важно видеть, застрял ли ролик. */
function SubmittedList({ task, now }: { task: Task; now: number }) {
  const rows = liveItems(task).filter((i) => i.versions.length > 0);
  return (
    <ModalSection title="Сдано" count={rows.length}>
      <div className="flex flex-col gap-1.5">
        {rows.map((it) => {
          const ver = lastVersion(it);
          const where =
            it.status === "uploaded" ? { text: taskModerated(task, it) ? "у модератора" : "ждёт контент-мейкера", cls: "text-review-fg" }
            : it.status === "moderated" ? { text: "норм · ждёт приёмки", cls: "text-review-fg" }
            : it.status === "approved" && it.autoPublishState === "failed" ? { text: "Instagram: не вышло", cls: "text-rework-fg" }
            : it.status === "approved" && it.autoPublishInstagram ? { text: "публикуется в Instagram", cls: "text-publish-fg" }
            : it.status === "approved" ? { text: "принято, ушло в Telegram", cls: "text-publish-fg" }
            : it.status === "published" ? { text: "опубликовано", cls: "text-done-fg" }
            : { text: "на доработке", cls: "text-rework-fg" };
          return (
            <div key={it.id} className="flex flex-col gap-1">
            <div className="flex items-center gap-2.5 rounded-full bg-muted/60 py-1.5 pl-3 pr-3 text-[13px]">
              <span className="min-w-0 flex-1 truncate">{it.title}</span>
              <span className="shrink-0 font-mono text-xs text-muted-foreground">v{ver?.n}</span>
              <span className={cn("shrink-0 text-xs font-medium", where.cls)}>{where.text}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{ver ? agoLong(ver.at, now) : ""}</span>
              <DownloadOriginal taskId={task.id} itemId={it.id} n={ver?.n} />
              {/* Отозвать — пока никто не посмотрел: отправил не тот файл,
                  забыл субтитры. Версия удаляется, видео снова «сдать». */}
              <WithdrawSubmission task={task} item={it} className="h-7 shrink-0 px-2.5 text-xs" />
              {/* «Снять» — в «⋯»: рядом с «Отозвать» его нажимали промахом (02.11.2026). */}
              {task.editor === ME_EDITOR && (it.status === "uploaded" || it.status === "moderated") && <VideoRemove task={task} item={it} variant="menu" />}
            </div>
            {(it.status === "uploaded" || it.status === "moderated") && <PublishAfterModeration task={task} item={it} className="pl-3" />}
            </div>
          );
        })}
      </div>
    </ModalSection>
  );
}


/**
 * Видео в свою публикацию. Публикация — «Паводки», видео в ней — сколько сняли: монтажёр
 * добавляет их сам, по одному, и сразу сдаёт — новое появляется в «Сдать».
 */
function AddVideoForm({ task }: { task: Task }) {
  const addItem = useStore((s) => s.addItem);
  const [title, setTitle] = useState("");
  const [format, setFormat] = useState<string>(PUB_FORMATS[0].value);
  const [busy, setBusy] = useState(false);
  return (
    <ModalSection title="Добавить видео в публикацию">
      <form
        className="flex flex-col gap-2"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!title.trim() || busy) return;
          setBusy(true);
          const id = await addItem(task.id, { title: title.trim(), format });
          setBusy(false);
          if (id) setTitle("");
        }}
      >
        <div className="flex items-center gap-2">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Название видео — например, «Штаб в Шымкенте»"
            className="h-10 min-w-0 flex-1 rounded-full border border-input bg-background px-4 text-[13px] outline-none placeholder:text-muted-foreground focus:ring-4 focus:ring-ring/20"
          />
          <Button type="submit" size="sm" disabled={!title.trim() || busy}>
            <Plus />
            Добавить
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PUB_FORMATS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFormat(f.value)}
              className={cn("h-7 rounded-full border px-3 text-xs font-medium", format === f.value ? "border-primary bg-primary text-primary-foreground" : "bg-background hover:bg-muted")}
            >
              {f.label}
            </button>
          ))}
        </div>
      </form>
    </ModalSection>
  );
}
